











import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import moment from 'moment';

import {
	Bullet,
	Circle,
	Color,
	color,
	Label,
	Legend,
	Modal,
	percent,
	Root,
	Scrollbar,
	Tooltip,
} from '@amcharts/amcharts5';
import {
	XYChart,
	LineSeries,
	DateAxis,
	ValueAxis,
	AxisRendererX,
	AxisRendererY,
	AxisRenderer,
	XYCursor,
} from '@amcharts/amcharts5/xy';

import {
	FeedApi,
	FeedTank,
	FeedTankGraphItem,
	Sensor,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';

import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { FeedTankGraphData } from '@/pages/account/products/feed/util/feed-tank-util';
import { TooltipLabelBuilder } from '@/pages/account/products/feed/util/tooltip-label-builder';


@Component({
	components: {
		DatePicker,
	},
})
export default class FeedTankStrainChart extends Vue {
	@Prop({ required: true, type: Object })
	private feedTank!: FeedTank;

	public get hasData(): boolean {
		return (this.graphData?.weight ?? []).length > 0;
	}

	public errorMessage?: string;
	public loading: boolean = true;
	/** Keys: 'weight' | 'reading/{sensorId}' */
	public graphData?: Record<string, FeedTankGraphItem[]>;


	private feedApi: FeedApi = new FeedApi();
	private sensorSeries: LineSeries[] = [];
	private weightSeries?: LineSeries;
	private noDataModal?: Modal;
	private xAxis?: DateAxis<AxisRenderer>;

	public async created(): Promise<void> {
		if (!this.feedTank.id) {
			this.errorMessage = 'Invalid feed tank provided';
			this.loading = false;
			return;
		}
		await this.fetchGraphData(this.feedTank.id);
		this.createChart();
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		const startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		const endDate = moment(newDateRange.end, 'DD-MM-YYYY').add(1, 'days');
		const offset = moment()
			.endOf('day')
			.diff(endDate, 'days');
		const nDays = endDate.diff(startDate, 'days') + 1;

		await this.fetchGraphData(this.feedTank.id!, offset, nDays);

		this.xAxis?.zoom(0, 1);
		this.sensorSeries.forEach((line, i) => {
			const key = this.getStrainReadingsKey(i);
			if (!key) return;

			const readings = this.graphData?.[key] ?? [];
			line.data.setAll(this.formatStrainReadings(readings));
		});

		this.weightSeries?.data.setAll(this.formatWeightData(this.graphData?.weight ?? []));

		if (!this.hasData) {
			this.noDataModal?.open();
		} else {
			this.noDataModal?.close();
		}

		this.xAxis?.setAll({ min: startDate.valueOf(), max: endDate.valueOf() });
	}

	private async fetchGraphData(
		feedTankId: number,
		daysOffset: number | undefined = 0,
		nDays: number | undefined = 14,
	): Promise<void> {
		try {
			const graphDataResponse = await this.feedApi.feedTanksIdGraphDataGet(
				feedTankId,
				nDays,
				'weight,reading',
				daysOffset?.toString(),
			);

			// @ts-ignore (API returns 'reading/xyz', openapi can't dynamically generate the sensor keys)
			const data = graphDataResponse.data.data as Record<string, FeedTankGraphItem[]> | undefined;

			this.graphData = data;
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to load graph data');
			this.errorMessage = errorResponse.errorMessage;
		} finally {
			this.loading = false;
		}
	}

	private getStrainReadingsKey(index: number): string | null {
		const sensors = this.feedTank.sensors ?? [];

		if (sensors[index]?.id) return `reading/${sensors[index].id}`;

		return null;
	}

	private sensorDisplayName(sensor: Sensor, index: number): string {
		return sensor.serialNumber // Human readable name
			?? sensor.uniqueId // Machine readable name
			?? `Sensor ${sensor.id ?? index + 1}`;
	}

	private formatStrainReadings(
		items: FeedTankGraphItem[],
	): { value: number; date: number }[] {
		return items.map(r => ({
			value: r.value ?? 0,
			date: new Date(r.date!).getTime(),
		}));
	}

	private formatWeightData(items: FeedTankGraphItem[]): { value: number; date: number; text: string }[] {
		return items.map(item => {
			const primary = new FeedTankGraphData(this.feedTank, item, true);
			const tonnes = primary.value / 1000;
			const roundedTonnes = Math.round(tonnes * 10) / 10;
			const text = roundedTonnes.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + ' t';
			return {
				value: tonnes,
				date: new Date(item.date!).getTime(),
				text,
			};
		});
	}

	private createChart(): void {
		const root: Root = Root.new('feedTankStrainChart');
		const chart: XYChart = XYChart.new(root, {
			panX: false,
			panY: false,
			wheelX: 'zoomX',
		});

		const {
			cursor,
			xAxis,
			yAxisReadings,
			yAxisWeight,
			sensorSeries,
			weightSeries,
			legend,
			noDataModal,
		} = this.createChartFeatures(root);

		this.xAxis = xAxis;
		this.sensorSeries = sensorSeries;
		this.weightSeries = weightSeries;
		this.noDataModal = noDataModal;

		chart.set('cursor', cursor);

		chart.xAxes.push(xAxis);
		chart.yAxes.push(yAxisReadings);
		chart.yAxes.push(yAxisWeight);
		sensorSeries.forEach(s => chart.series.push(s));
		chart.series.push(weightSeries);

		const scrollbarX = Scrollbar.new(root, { orientation: 'horizontal' });
		chart.set('scrollbarX', scrollbarX);

		chart.bottomAxesContainer.children.push(legend);

		const sensors = this.feedTank.sensors ?? [];
		sensors.slice(0, 2).forEach((_s, i) => {
			const key = this.getStrainReadingsKey(i);
			if (!key) return;

			const readings = this.graphData?.[key] ?? [];
			this.sensorSeries[i]?.data.setAll(this.formatStrainReadings(readings));
		});

		this.weightSeries?.data.setAll(this.formatWeightData(this.graphData?.weight ?? []));

		legend.data.setAll(chart.series.values);

		root.container.children.push(chart);

		sensorSeries.forEach(s => s.appear(1000));
		weightSeries.appear(1000);
		chart.appear(1000, 100);
	}

	private createChartFeatures(root: Root): {
		cursor: XYCursor;
		xAxis: DateAxis<AxisRenderer>;
		yAxisReadings: ValueAxis<AxisRenderer>;
		yAxisWeight: ValueAxis<AxisRenderer>;
		sensorSeries: LineSeries[];
		weightSeries: LineSeries;
		legend: Legend;
		noDataModal: Modal;
	} {
		const xAxis: DateAxis<AxisRenderer> = DateAxis.new(root, {
			baseInterval: { timeUnit: 'minute', count: 15 },
			renderer: AxisRendererX.new(root, {}),
			tooltip: Tooltip.new(root, {}),
			min: Date.now() - 86400 * 1000 * 14,
			max: Date.now(),
		});

		const yAxisReadings: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {}),
			min: 0,
		});

		yAxisReadings.get("renderer")?.grid.template.set("visible", false);

		const nominalWeightKg = (this.feedTank.nominalWeight ?? 1000) * (this.feedTank.numTanks ?? 1);
		const nominalWeightTonnes = nominalWeightKg / 1000;
		const yAxisWeight: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, { opposite: true }),
			min: 0,
			max: nominalWeightTonnes * 1.1,
		});
		yAxisWeight.children.push(Label.new(root, {
			text: 'Tonnes',
			rotation: -90,
			y: percent(50),
			centerX: percent(50),
		}));
		yAxisWeight.get("renderer")?.grid.template.set("visible", false);

		const cursor: XYCursor = XYCursor.new(root, { behavior: 'zoomX' });
		cursor.lineX.set('visible', true);
		cursor.lineY.set('visible', true);

		const sensors = this.feedTank.sensors ?? [];
		const sensorFillColors = [color('#1e88e5'), color('#43a047')];
		const weightBulletColor = Color.fromRGB(213, 62, 79);

		const sensorSeries: LineSeries[] = sensors.slice(0, 2).map((s, i) => {
			const fillColor = sensorFillColors[i] ?? color('#666');
			const sensorName = this.sensorDisplayName(s, i);
			const tooltip = Tooltip.new(root, {
				getFillFromSprite: false,
				labelText: new TooltipLabelBuilder()
					.line('{name}', { fontSize: 12 })
					.line('{valueY}', { fontSize: 14, bold: true })
					.line('{valueX.formatDate("yyyy-MM-dd HH:mm:ss")}', {
						color: '#4a4a4a',
						fontSize: 10,
					})
					.build(),
			});
			tooltip.get('background')?.setAll({
				fill: fillColor,
				fillOpacity: 0.8,
				strokeOpacity: 0,
			});

			const series = LineSeries.new(root, {
				name: sensorName,
				xAxis,
				yAxis: yAxisReadings,
				valueXField: 'date',
				valueYField: 'value',
				connect: false,
				snapTooltip: true,
				showTooltipOn: 'always',
				autoGapCount: 24,
				stroke: fillColor,
				tooltip: tooltip,
			});

			return series;
		});

		const weightTooltip = Tooltip.new(root, {
			getFillFromSprite: false,
			labelText: new TooltipLabelBuilder()
				.line('Tank Weight', { fontSize: 12 })
				.line('{text}', { fontSize: 14, bold: true })
				.line('{valueX.formatDate("yyyy-MM-dd HH:mm:ss")}', {
					color: '#e8e8e8',
					fontSize: 10,
				})
				.build(),
		});
		weightTooltip.get('background')?.setAll({
			fill: weightBulletColor,
			fillOpacity: 0.8,
			strokeOpacity: 0,
		});

		const weightSeries: LineSeries = LineSeries.new(root, {
			name: 'Tank Weight',
			xAxis,
			yAxis: yAxisWeight,
			valueXField: 'date',
			valueYField: 'value',
			connect: false,
			snapTooltip: true,
			showTooltipOn: 'always',
			tooltip: weightTooltip,
		});

		weightSeries.bullets.push(r =>
			Bullet.new(r, {
				locationX: 0.5,
				locationY: 0.5,
				sprite: Circle.new(r, {
					radius: 5,
					fill: weightBulletColor,
					stroke: Color.fromRGB(255, 255, 255),
					strokeWidth: 2,
				}),
			}),
		);
		weightSeries.set('fill', weightBulletColor);
		
		const legend: Legend = Legend.new(root, {
			x: percent(50),
			centerX: percent(50),
		});
		const noDataModal: Modal = Modal.new(root, {
			content: 'No graph data for this feed tank, over this range',
		});
		return {
			cursor,
			xAxis,
			yAxisReadings,
			yAxisWeight,
			sensorSeries,
			weightSeries,
			legend,
			noDataModal,
		};
	}
}
